<template>
  <div class="map-csv-columns-table">
    <v-switch
      color="brandSecondary"
      v-model="includesHeaderRow"
      label="File includes a header row"
      hide-details
    />
    <Typography variant="subtitle-1" v-if="showRowCount"
      >Total rows: {{ usedRows.length }}</Typography
    >
    <Typography variant="subtitle-1" v-if="usedRows.length === 0" class="error--text">
      The uploaded file is empty
    </Typography>
    <Typography variant="subtitle-1" v-if="usedRows.length > 0 && showRequiredColumns">
      You'll need to map these fields: {{ headerNames }}. Any columns not selected will not be
      uploaded.
    </Typography>

    <div v-if="usedRows.length > 0" class="csv-table">
      <div
        v-for="rowIdx in usedRows.length + 1"
        class="table-row"
        :class="{ 'table-row--with-delete': showDeleteRowButton }"
        :key="rowIdx"
      >
        <v-btn
          v-if="showDeleteRowButton"
          icon
          small
          color="red"
          title="Delete row"
          @click="deleteRow(rowIdx - 2)"
          class="align-self-center"
          :disabled="rowIdx === 1"
        >
          <v-icon>mdi-trash-can</v-icon>
        </v-btn>
        <div v-if="rowIdx === 1" class="column-selectors header-border">
          <div v-for="(_, colIndex) in rows.length > 0 ? rows[0].length : 0" :key="colIndex">
            <v-autocomplete
              :items="columns"
              color=""
              item-text="label"
              item-value="value"
              label="Column label"
              hide-details
              v-model="selectedHeaders[colIndex]"
              @input="onHeaderChanged(colIndex, $event)"
            >
              <template v-slot:item="{ item }">
                <span
                  :class="{
                    'v-list-item--disabled theme--light': !!selectedHeaders.find(
                      (x) => x == item.value
                    ),
                  }"
                >
                  {{ item.label }}
                </span>
              </template>
            </v-autocomplete>
          </div>
        </div>
        <div v-else class="csv-row row-border">
          <div v-for="(value, colIdx) in usedRows[rowIdx - 2]" :key="colIdx" class="csv-col">
            <Typography variant="body-1">{{ value }}</Typography>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Typography from "@/components/generic/Typography";

export default {
  name: "MapCSVColumnsTable",
  components: {
    Typography,
  },
  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    columns: {
      type: Array,
      default: () => [],
    },
    showDeleteRowButton: {
      type: Boolean,
      default: false,
    },
    showRowCount: {
      type: Boolean,
      default: false,
    },
    showRequiredColumns: {
      type: Boolean,
      default: false,
    },
    autoMapColumns: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["mapped-csv-columns-updated", "delete-row"],
  data() {
    return {
      includesHeaderRow: true,
      selectedHeaders: [],
    };
  },
  computed: {
    usedRows() {
      return this.includesHeaderRow ? this.rows.slice(1) : [...this.rows];
    },
    headerNames() {
      return this.columns.map((x) => x.label || x.text).join(", ");
    },
    mappedRowsWithHeaders() {
      let results = [];
      if (!Object.keys(this.selectedHeaders).length) return results;

      this.usedRows.forEach((row, index) => {
        let obj = { __row: this.includesHeaderRow ? index + 1 : index };
        Object.entries(this.selectedHeaders).forEach(([index, columnKey]) => {
          if (columnKey !== undefined) {
            obj[columnKey] = row[index];
          }
        });
        results.push(obj);
      });
      return results;
    },
  },
  methods: {
    deleteRow(index) {
      if (this.includesHeaderRow) index++;
      this.$emit("delete-row", index);
    },
    onHeaderChanged(changedIndex, value) {
      this.selectedHeaders.forEach((key, headerIndex) => {
        if (key !== undefined && headerIndex != changedIndex && key == value)
          this.selectedHeaders[headerIndex] = undefined;
      });
    },
    performAutoMap() {
      if (this.rows.length === 0 || this.columns.length === 0) return;

      const colCount = this.rows[0].length;
      const newHeaders = [];

      for (let i = 0; i < colCount && i < this.columns.length; i++) {
        newHeaders[i] = this.columns[i].value;
      }

      this.selectedHeaders = newHeaders;
    },
  },
  watch: {
    mappedRowsWithHeaders: {
      handler(newVal) {
        this.$emit("mapped-csv-columns-updated", newVal);
      },
    },
    rows: {
      immediate: true,
      handler() {
        if (this.autoMapColumns) this.performAutoMap();
      },
    },
    columns: {
      handler() {
        if (this.autoMapColumns) this.performAutoMap();
      },
    },
  },
};
</script>

<style scoped>
.map-csv-columns-table {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  justify-content: flex-start;
  width: 100%;
  gap: 0.5rem;
}

.map-csv-columns-table > *:first-child {
  margin: 0 !important;
}

.csv-table {
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
  align-items: stretch;
  overflow-y: scroll;
  overflow-x: scroll;
  border-right: 1px solid black;
}

.csv-row {
  display: flex;
  flex-direction: row;
  justify-content: stretch;
  align-items: stretch;
  width: 100%;
}

.column-selectors {
  display: flex;
  flex-direction: row;
  border-top: 1px solid black;
  width: 100%;
}

.column-selectors > * {
  flex: 1;
  min-width: 150px;
  border-right: 1px solid black;
  padding: 1rem 0.5rem 1.5rem 0.5rem;
  background-color: var(--v-lightGrey-base);
  border-bottom: 1px solid black;
}

.column-selectors > * > * {
  padding: 0;
}

.column-selectors > *:last-child {
  border-right: none;
}

.column-selectors > *:first-child {
  border-left: 1px solid black;
}

.csv-col {
  flex: 1;
  min-width: 150px;
  padding: 0.5rem;
  border-right: 1px solid black;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
  display: flex;
  align-items: center;
  align-self: stretch;
  border-bottom: 1px solid black;
}

.csv-col:first-child {
  border-left: 1px solid black;
}

.csv-col:last-child {
  border-right: none;
  border-bottom: 1px solid black;
}

.table-row {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  min-width: fit-content;
  gap: 0.5rem;
}

.table-row--with-delete > *:first-child {
  border: none;
}

.table-row--with-delete:first-child > button {
  visibility: hidden;
}
</style>
